import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Gauge } from "lucide-react";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import { aiModelHealth, humanReviewQueue, crowdTrendData } from "../data/mockData";
import { STATUS, statusClasses } from "../data/statusColors";
import { useTranslate } from "../i18n/useTranslate";

// ── real-data wiring, not display copy ──────────────────────────────
// The human review queue and the model health table use different-length
// labels for the same two models — a deliberate compact-vs-full choice made
// when this data was translated (queue rows are tight on space; the health
// table isn't) — so an exact string match on `model` would silently miss
// two of the six queue items. This is the lookup that repairs that.
const MODEL_ALIAS = {
  "Transport Surge": "Transport Surge Forecast",
  "Service Load": "Service Load Predictor",
};
const findModel = (name) => aiModelHealth.find(m => m.model === (MODEL_ALIAS[name] || name));

// Which page's numbers each model actually powers — grounded in subject
// matter (a heatstroke model's confidence shows up wherever heatstroke risk
// is displayed), since none of these pages print the model's name directly.
// This is the missing piece the rest of the platform never states: a bare
// "91% confident" badge with nowhere to ask what produced it.
const MODEL_LINKS = {
  "Crowd Density CNN":        [{ to: "/crowd-intelligence", label: "Crowd Risk Engine" }, { to: "/command", label: "War Room" }],
  "Stampede Risk LSTM":       [{ to: "/crowd-intelligence", label: "Crowd Risk Engine" }],
  "Missing Person Match":     [{ to: "/missing", label: "Missing Persons" }, { to: "/field-ops", label: "Field Copilot" }],
  "Alert Dedup Engine":       [{ to: "/ai-assurance", label: "AI Stack Assurance" }],
  "Heatstroke Predictor":     [{ to: "/health", label: "Health & Epidemic" }],
  "Transport Surge Forecast": [{ to: "/mobility", label: "Mobility Intelligence" }],
  "Service Load Predictor":   [{ to: "/services", label: "Service Stress" }, { to: "/pilgrim-support", label: "Pilgrim Guidance" }],
};

const statusBadge = s =>
  s === "Healthy" ? "badge badge-success" :
  s === "Monitoring" ? "badge badge-warning" : "badge badge-critical";

// ── component ─────────────────────────────────────────────────────
export default function ModelTrust() {
  const t = useTranslate();

  const avgAccuracy   = (aiModelHealth.reduce((s, m) => s + m.accuracy, 0) / aiModelHealth.length).toFixed(1);
  const needAttention = aiModelHealth.filter(m => m.status !== "Healthy").length;
  const totalReviewed = aiModelHealth.reduce((s, m) => s + m.reviewed, 0);

  // Today's crowd forecast accuracy, computed from the same crowdTrendData
  // every other page reads — mean absolute % error across the hours that
  // have an actual figure to check the forecast against, expressed as an
  // accuracy so it reads next to the model accuracies above it.
  const settled = crowdTrendData.filter(d => d.actual !== null);
  const meanErrorPct = settled.reduce((s, d) => s + Math.abs(d.actual - d.predicted) / d.actual, 0) / settled.length * 100;
  const forecastAccuracy = (100 - meanErrorPct).toFixed(1);

  return (
    <div className="space-y-8">
      <SectionHeader icon="Gauge" title="AI Trust Centre"
        subtitle="Every AI confidence score shown across the platform, paired with the issuing model's real track record — the answer to 'how good has this model actually been'"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard title="Models Monitored"   value={aiModelHealth.length.toString()} icon="Brain"         color="blue"   index={0} live />
        <MetricCard title="Avg Model Accuracy" value={`${avgAccuracy}%`}                icon="Target"        color="green"  index={1} />
        <MetricCard title="Needing Attention"  value={needAttention.toString()}         icon="AlertTriangle" color="amber"  index={2} />
        <MetricCard title="Total AI Reviews"   value={totalReviewed.toLocaleString()}   icon="Eye"           color="purple" index={3} />
      </div>

      {/* Live confidence, paired with track record */}
      <ChartCard title="Live Confidence, Paired With Track Record"
        subtitle="Every item in today's human review queue, next to how right its model has actually been" live>
        <div className="space-y-2">
          {humanReviewQueue.map((item, i) => {
            const model = findModel(item.model);
            return (
              <motion.div key={item.id}
                initial={{ opacity:0, x:-6 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                className="flex items-center justify-between gap-4 p-3 rounded-xl border border-slate-200 hover:border-blue-200 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-slate-800 leading-snug truncate">{t(item.alert)}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{t(item.model)} · {item.assignee}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 text-center">
                  <div>
                    <div className="text-sm font-black text-blue-700">{item.confidence}%</div>
                    <div className="text-[9px] text-slate-400">{t("this time")}</div>
                  </div>
                  <div className="text-slate-300">·</div>
                  {model ? (
                    <div>
                      <div className={`text-sm font-black ${model.accuracy >= 90 ? "text-emerald-600" : "text-amber-600"}`}>{model.accuracy}%</div>
                      <div className="text-[9px] text-slate-400">{t("lifetime accuracy")}</div>
                    </div>
                  ) : (
                    <div className="text-[9px] text-slate-400">{t("no track record yet")}</div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </ChartCard>

      {/* Model trust ledger */}
      <ChartCard title="Model Trust Ledger"
        subtitle="Every production model, its track record, and which page's numbers it actually powers">
        <div className="overflow-x-auto">
          <table className="gov-table">
            <thead>
              <tr>
                <th>{t("Model")}</th><th>{t("Accuracy")}</th><th>{t("Drift")}</th>
                <th>{t("Bias")}</th><th>{t("Status")}</th><th>{t("Powers")}</th>
              </tr>
            </thead>
            <tbody>
              {aiModelHealth.map((m, i) => (
                <motion.tr key={m.model} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                  <td className="font-bold text-slate-900">{t(m.model)}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-1.5 bg-emerald-500 rounded-full" style={{ width:`${m.accuracy}%` }} />
                      </div>
                      <span className="font-bold text-xs">{m.accuracy}%</span>
                    </div>
                  </td>
                  <td><span className={`text-xs font-bold ${m.drift > 1.5 ? STATUS.warning.text : STATUS.good.text}`}>{m.drift}%</span></td>
                  <td><span className={`badge ${m.bias === "Low" ? "badge-success" : m.bias === "Medium" ? "badge-warning" : "badge-critical"}`}>{t(m.bias)}</span></td>
                  <td><span className={statusBadge(m.status)}>{t(m.status)}</span></td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {(MODEL_LINKS[m.model] || []).map(l => (
                        <Link key={l.to} to={l.to}
                          className="inline-flex items-center gap-0.5 text-[9px] font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 px-1.5 py-0.5 rounded-full transition-colors">
                          {t(l.label)}<ArrowRight size={9} />
                        </Link>
                      ))}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Today's forecast accuracy */}
      <ChartCard title="Today's Crowd Forecast Accuracy"
        subtitle="Computed from the same actual-vs-predicted numbers shown on Crowd Risk Engine and War Room — gathered here as a running track record">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-1 flex flex-col items-center justify-center p-4 rounded-xl bg-emerald-50 border border-emerald-200">
            <div className="text-3xl font-black text-emerald-600">{forecastAccuracy}%</div>
            <div className="text-[10px] text-slate-500 mt-1 text-center">{t("accurate across the")} {settled.length} {t("hours checked so far today")}</div>
          </div>
          <div className="sm:col-span-2 flex flex-col justify-center">
            <p className="text-xs text-slate-600 leading-relaxed">
              {t("Every hour where an actual pilgrim count has come in is compared against what the model predicted for that same hour, before the count was known. This is the same forecast that drives the risk badges on Crowd Risk Engine and War Room — shown here on its own, as a number that can be checked against reality, rather than a confidence score with nowhere to be verified.")}
            </p>
            <Link to="/digital-twin#model-calibration"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-700 hover:text-blue-800 mt-3 w-fit">
              {t("See the longer-run track record across past Kumbh events")} <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </ChartCard>
    </div>
  );
}
